/**
 * E-invoicing: Factur-X/ZUGFeRD and XRechnung.
 *
 * One semantic model, two syntaxes. `@e-invoice-eu/core` takes an EN 16931
 * invoice expressed in UBL terms and emits either UBL or CII, and for Factur-X
 * it embeds the CII into a PDF/A-3 we render ourselves.
 *
 * On timing: receiving e-invoices is already obligatory, but issuing them for
 * domestic B2B only becomes mandatory on 01.01.2028 (01.01.2027 above EUR 800k
 * turnover), and § 34a UStDV exempts Kleinunternehmer permanently. This is
 * built for readiness, so nothing here is on the critical path of getting an
 * invoice out of the door.
 *
 * Nothing in this file validates conformance. Both veraPDF and Mustangproject
 * are Java, so authoritative validation runs in CI against golden fixtures —
 * see `ci/einvoice/`. What runs here is a pre-flight check for the handful of
 * EN 16931 business rules a user can actually do something about.
 */

import type { CompanySettings, EInvoiceFormat, Invoice } from '@/domain/types';
import { formatAmount, lineNetCents, summariseVat } from '@/domain/money';
import { exemptionNote, vatCategoryCode } from '@/domain/vat';
import { de } from '@/i18n/de';

/** The library's format identifier for each of our output choices. */
const LIBRARY_FORMAT: Record<Exclude<EInvoiceFormat, 'keine'>, string> = {
  facturx: 'Factur-X-EN16931',
  'xrechnung-ubl': 'XRECHNUNG-UBL',
  'xrechnung-cii': 'XRECHNUNG-CII',
};

/** Format names are the same words in both languages; the German copy is canonical. */
export const E_INVOICE_FORMAT_LABELS: Record<EInvoiceFormat, string> = de.labels.eInvoice;

export interface EInvoiceResult {
  blob: Blob;
  filename: string;
  mimeType: 'application/pdf' | 'application/xml';
}

export class EInvoiceIncompleteError extends Error {
  constructor(readonly problems: string[]) {
    super(problems.join(' '));
    this.name = 'EInvoiceIncompleteError';
  }
}

/**
 * Pre-flight the EN 16931 rules that fail on missing user data rather than on
 * a bug. The library's own validation reports JSON-schema paths, which is not
 * something to put in front of a driver.
 */
export function checkEInvoiceReadiness(
  invoice: Invoice,
  company: CompanySettings,
): string[] {
  const problems: string[] = [];

  if (!invoice.invoiceNumber) {
    problems.push('Die Rechnung ist noch nicht gestellt und hat keine Rechnungsnummer.');
  }
  if (!company.email?.trim()) {
    // BT-34. Without it the seller has no electronic address and BR-62 fails.
    problems.push('In den Einstellungen fehlt die E-Mail-Adresse des Unternehmens (BT-34).');
  }
  if (!invoice.customerEmail?.trim()) {
    problems.push('Die E-Mail-Adresse des Rechnungsempfängers fehlt (BT-49).');
  }
  if (!invoice.customerName.trim() || !invoice.customerAddress.trim()) {
    // The § 33 UStDV short form has no counterpart in EN 16931: a structured
    // invoice always carries the full buyer party.
    problems.push('Name und Anschrift des Empfängers sind für eine E-Rechnung immer erforderlich.');
  }
  if (!parsePostalAddress(invoice.customerAddress).city) {
    problems.push('Aus der Empfängeranschrift lässt sich kein Ort lesen (Format: Straße, dann "PLZ Ort").');
  }
  if (!company.vatId?.trim() && !company.taxNumber?.trim()) {
    problems.push('Steuernummer oder USt-IdNr. des Unternehmens fehlt (BT-31 / BT-32).');
  }
  if (!company.street.trim() || !company.postalCode.trim() || !company.city.trim()) {
    problems.push('Die Anschrift des Unternehmens ist unvollständig (BG-5).');
  }

  return problems;
}

export async function generateEInvoice(
  invoice: Invoice,
  company: CompanySettings,
  format: Exclude<EInvoiceFormat, 'keine'>,
): Promise<EInvoiceResult> {
  const problems = checkEInvoiceReadiness(invoice, company);
  if (problems.length > 0) throw new EInvoiceIncompleteError(problems);

  // Loaded on demand: the library plus its schemas is the single largest thing
  // in the bundle, and most sessions never issue an e-invoice.
  const { InvoiceService } = await import('@e-invoice-eu/core');

  const libraryFormat = LIBRARY_FORMAT[format];
  const document = buildEn16931Invoice(invoice, company);
  const service = new InvoiceService(SILENT_LOGGER);

  const options: Parameters<InvoiceService['generate']>[1] = {
    format: libraryFormat,
    lang: 'de',
    noWarnings: true,
  };

  if (format === 'facturx') {
    const { buildInvoicePdfDocument } = await import('./invoicePdf');
    const pdf = await buildInvoicePdfDocument(invoice, company);
    options.pdf = {
      buffer: await pdf.save(),
      filename: `${safeName(invoice.invoiceNumber)}.pdf`,
      mimetype: 'application/pdf',
    };
  }

  const output = await service.generate(document, options);

  if (typeof output === 'string') {
    return {
      blob: new Blob([output], { type: 'application/xml;charset=utf-8' }),
      filename: `${safeName(invoice.invoiceNumber)}-xrechnung.xml`,
      mimeType: 'application/xml',
    };
  }

  return {
    blob: new Blob([output as unknown as BlobPart], { type: 'application/pdf' }),
    filename: `${safeName(invoice.invoiceNumber)}-facturx.pdf`,
    mimeType: 'application/pdf',
  };
}

// ---------------------------------------------------------------------------
// Mapping
// ---------------------------------------------------------------------------

type LibraryInvoice = import('@e-invoice-eu/core').Invoice;
type InvoiceService = import('@e-invoice-eu/core').InvoiceService;

/** Amounts cross the boundary as decimal strings with a point, never a comma. */
function money(cents: number): string {
  return (cents / 100).toFixed(2);
}

function safeName(value: string): string {
  return value.replace(/[^\w.-]+/g, '_') || 'rechnung';
}

export interface PostalAddress {
  street: string;
  postalCode: string;
  city: string;
  countryCode: LibraryCountryCode;
}

/**
 * Read a free-text German address block into its EN 16931 parts.
 *
 * The invoice form stores the recipient the way it is printed — street on one
 * line, `PLZ Ort` on the next — because that is what goes in the envelope
 * window. A structured invoice needs the pieces separately.
 */
export function parsePostalAddress(block: string): PostalAddress {
  const lines = block
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  let country: LibraryCountryCode = 'DE';
  const last = lines[lines.length - 1];
  if (last && /^(deutschland|germany|österreich|austria|schweiz|switzerland)$/i.test(last)) {
    country = /öster|austria/i.test(last) ? 'AT' : /schweiz|switz/i.test(last) ? 'CH' : 'DE';
    lines.pop();
  }

  // The postal line is the last one that starts with a postcode.
  let postalCode = '';
  let city = '';
  let postalIndex = -1;
  for (let index = lines.length - 1; index >= 0; index -= 1) {
    const match = /^([A-Z]{1,2}-)?(\d{4,5})\s+(.+)$/.exec(lines[index]!);
    if (match) {
      postalCode = match[2]!;
      city = match[3]!;
      postalIndex = index;
      break;
    }
  }

  const street = lines.filter((_, index) => index !== postalIndex).join(', ');

  return { street, postalCode, city, countryCode: country };
}

/**
 * VAT category per line and per breakdown group.
 *
 * EN 16931 rule BR-E-10 requires an exemption reason whenever the category is
 * `E`, which is where § 19 UStG lands — so the same sentence that has to be
 * printed on the paper invoice goes into the XML.
 */
function vatCategory(ratePercent: number, kleinunternehmer: boolean) {
  const code = vatCategoryCode(kleinunternehmer ? 0 : ratePercent, kleinunternehmer);
  return {
    code,
    rate: code === 'S' ? String(ratePercent) : '0',
    exemptionReason: code === 'E' ? exemptionNote(0, true) ?? undefined : undefined,
  };
}

export function buildEn16931Invoice(
  invoice: Invoice,
  company: CompanySettings,
): LibraryInvoice {
  const buyer = parsePostalAddress(invoice.customerAddress);
  const kleinunternehmer = invoice.isKleinunternehmer;
  const isCreditNote = Boolean(invoice.cancelsInvoiceId);

  /**
   * A Stornorechnung is stored with negative amounts, because that is what
   * makes it net out against the invoice it reverses and what a German reader
   * expects to see printed. EN 16931 says it differently: a credit note is
   * type 381 with positive amounts, and BR-27 rejects a negative item price
   * outright. The document type already carries the direction, so the sign is
   * dropped here rather than in the model.
   */
  const amount = (cents: number) => (isCreditNote ? Math.abs(cents) : cents);

  const buckets = summariseVat(
    invoice.lines.map((line) => ({
      netCents: amount(lineNetCents(line.quantity, line.unitPriceNetCents)),
      vatRatePercent: kleinunternehmer ? 0 : line.vatRatePercent,
    })),
  );

  const lineNets = invoice.lines.map((line) =>
    amount(lineNetCents(line.quantity, line.unitPriceNetCents)),
  );
  const netTotal = lineNets.reduce((sum, value) => sum + value, 0);
  const vatTotal = buckets.reduce((sum, bucket) => sum + bucket.vatCents, 0);

  const document: LibraryInvoice = {
    'ubl:Invoice': {
      'cbc:ID': invoice.invoiceNumber,
      'cbc:IssueDate': invoice.issueDate,
      'cbc:DueDate': invoice.dueDate,
      // 380 commercial invoice, 381 credit note. A Stornorechnung carries
      // negative amounts, and EN 16931 rule BR-CO-15 rejects those on a 380.
      'cbc:InvoiceTypeCode': isCreditNote ? '381' : '380',
      'cbc:DocumentCurrencyCode': 'EUR',
      // BR-DE-15 makes BT-10 mandatory for XRechnung. Falling back to the
      // invoice number is the convention when the buyer supplied nothing.
      'cbc:BuyerReference': invoice.buyerReference?.trim() || invoice.invoiceNumber,
      'cac:InvoicePeriod': {
        'cbc:StartDate': invoice.serviceDateFrom,
        'cbc:EndDate': invoice.serviceDateTo,
      },
      'cac:AccountingSupplierParty': {
        'cac:Party': {
          'cbc:EndpointID': company.email ?? '',
          'cbc:EndpointID@schemeID': 'EM',
          ...sellerIdentification(company),
          'cac:PostalAddress': {
            'cbc:StreetName': company.street,
            'cbc:CityName': company.city,
            'cbc:PostalZone': company.postalCode,
            'cac:Country': { 'cbc:IdentificationCode': countryCode(company.country) },
          },
          'cac:PartyTaxScheme': sellerTaxSchemes(company),
          'cac:PartyLegalEntity': {
            'cbc:RegistrationName': company.companyName,
            ...(company.registrationNumber?.trim()
              ? { 'cbc:CompanyID': company.registrationNumber.trim() }
              : {}),
          },
          'cac:Contact': {
            'cbc:Name': company.ownerName || company.companyName,
            ...(company.phone ? { 'cbc:Telephone': company.phone } : {}),
            ...(company.email ? { 'cbc:ElectronicMail': company.email } : {}),
          },
        },
      },
      'cac:AccountingCustomerParty': {
        'cac:Party': {
          'cbc:EndpointID': invoice.customerEmail ?? '',
          'cbc:EndpointID@schemeID': 'EM',
          'cac:PostalAddress': {
            'cbc:StreetName': buyer.street,
            'cbc:CityName': buyer.city,
            'cbc:PostalZone': buyer.postalCode,
            'cac:Country': { 'cbc:IdentificationCode': buyer.countryCode },
          },
          ...(invoice.customerVatId
            ? {
                'cac:PartyTaxScheme': {
                  'cbc:CompanyID': invoice.customerVatId,
                  'cac:TaxScheme': { 'cbc:ID': 'VAT' },
                },
              }
            : {}),
          'cac:PartyLegalEntity': {
            'cbc:RegistrationName': invoice.customerName,
          },
        },
      },
      // BT-72. Optional in EN 16931, but the CII schema puts
      // ApplicableHeaderTradeDelivery before the settlement group and rejects
      // the document outright when it is absent — so what is optional in the
      // semantic model is mandatory in the syntax. The last day of the service
      // period is when the transport was performed.
      'cac:Delivery': { 'cbc:ActualDeliveryDate': invoice.serviceDateTo },
      ...(company.iban
        ? {
            'cac:PaymentMeans': [
              {
                // 58 = SEPA credit transfer.
                'cbc:PaymentMeansCode': '58' as const,
                'cbc:PaymentID': invoice.invoiceNumber,
                'cac:PayeeFinancialAccount': {
                  'cbc:ID': company.iban,
                  ...(company.bankName ? { 'cbc:Name': company.bankName } : {}),
                  ...(company.bic
                    ? { 'cac:FinancialInstitutionBranch': { 'cbc:ID': company.bic } }
                    : {}),
                },
              },
            ],
          }
        : {}),
      'cac:PaymentTerms': { 'cbc:Note': invoice.paymentTerms },
      'cac:TaxTotal': [
        {
          'cbc:TaxAmount': money(vatTotal),
          'cbc:TaxAmount@currencyID': 'EUR',
          'cac:TaxSubtotal': buckets.map((bucket) => {
            const category = vatCategory(bucket.ratePercent, kleinunternehmer);
            return {
              'cbc:TaxableAmount': money(bucket.netCents),
              'cbc:TaxableAmount@currencyID': 'EUR' as const,
              'cbc:TaxAmount': money(Math.abs(bucket.vatCents)),
              'cbc:TaxAmount@currencyID': 'EUR' as const,
              'cac:TaxCategory': {
                'cbc:ID': category.code,
                'cbc:Percent': category.rate,
                ...(category.exemptionReason
                  ? { 'cbc:TaxExemptionReason': category.exemptionReason }
                  : {}),
                'cac:TaxScheme': { 'cbc:ID': 'VAT' },
              },
            };
          }),
        },
      ],
      'cac:LegalMonetaryTotal': {
        'cbc:LineExtensionAmount': money(netTotal),
        'cbc:LineExtensionAmount@currencyID': 'EUR',
        'cbc:TaxExclusiveAmount': money(netTotal),
        'cbc:TaxExclusiveAmount@currencyID': 'EUR',
        'cbc:TaxInclusiveAmount': money(netTotal + vatTotal),
        'cbc:TaxInclusiveAmount@currencyID': 'EUR',
        'cbc:PayableAmount': money(netTotal + vatTotal),
        'cbc:PayableAmount@currencyID': 'EUR',
      },
      'cac:InvoiceLine': invoice.lines.map((line, index) => {
        const category = vatCategory(
          kleinunternehmer ? 0 : line.vatRatePercent,
          kleinunternehmer,
        );
        return {
          'cbc:ID': String(index + 1),
          'cbc:InvoicedQuantity': String(line.quantity),
          // C62 = "one", the code for a countable unit without a dimension.
          'cbc:InvoicedQuantity@unitCode': 'C62' as const,
          'cbc:LineExtensionAmount': money(lineNets[index] ?? 0),
          'cbc:LineExtensionAmount@currencyID': 'EUR' as const,
          'cac:Item': {
            // BT-153 has to be a single line; the route detail goes to BT-154.
            'cbc:Name': firstLine(line.description),
            ...(restLines(line.description)
              ? { 'cbc:Description': restLines(line.description) }
              : {}),
            'cac:ClassifiedTaxCategory': {
              'cbc:ID': category.code,
              'cbc:Percent': category.rate,
              'cac:TaxScheme': { 'cbc:ID': 'VAT' },
            },
          },
          'cac:Price': {
            'cbc:PriceAmount': money(amount(line.unitPriceNetCents)),
            'cbc:PriceAmount@currencyID': 'EUR' as const,
          },
        };
      }) as LibraryInvoice['ubl:Invoice']['cac:InvoiceLine'],
      'cbc:Note': notes(invoice, company),
    },
  };

  return document;
}

/**
 * BT-29, and only when nothing else will identify the seller.
 *
 * Rule BR-CO-26 wants at least one of BT-29 (seller identifier), BT-30 (legal
 * registration identifier) or BT-31 (USt-IdNr.). A Kleinunternehmer has no
 * USt-IdNr. by definition, and a sole trader has no Handelsregisternummer
 * either, so without a fallback § 19 UStG would make a conforming e-invoice
 * impossible. The Steuernummer already appears as BT-32; repeating it as the
 * seller identifier is the convention German issuers settled on.
 */
function sellerIdentification(company: CompanySettings) {
  if (company.vatId?.trim() || company.registrationNumber?.trim()) return {};
  const taxNumber = company.taxNumber?.trim();
  if (!taxNumber) return {};
  return { 'cac:PartyIdentification': [{ 'cbc:ID': taxNumber }] };
}

function sellerTaxSchemes(company: CompanySettings) {
  const schemes: Array<{ 'cbc:CompanyID': string; 'cac:TaxScheme': { 'cbc:ID': string } }> = [];
  if (company.vatId?.trim()) {
    schemes.push({ 'cbc:CompanyID': company.vatId.trim(), 'cac:TaxScheme': { 'cbc:ID': 'VAT' } });
  }
  if (company.taxNumber?.trim()) {
    // FC is the code for a national tax registration identifier — the German
    // Steuernummer, which is what a Kleinunternehmer usually has instead.
    schemes.push({ 'cbc:CompanyID': company.taxNumber.trim(), 'cac:TaxScheme': { 'cbc:ID': 'FC' } });
  }
  return schemes as [] | [(typeof schemes)[0]] | [(typeof schemes)[0], (typeof schemes)[0]];
}

/**
 * BT-22, as a single entry.
 *
 * The library maps an array of notes onto one CII `ram:IncludedNote` holding
 * several `ram:Content` children, which fails both the CII schema and rule
 * CII-SR-030 — a note may carry exactly one content. Joining them into one
 * note reads identically to a human and is valid in both syntaxes.
 */
function notes(invoice: Invoice, company: CompanySettings): [string] | [] {
  const collected: string[] = [];
  if (invoice.isKleinunternehmer) {
    collected.push('Kein Ausweis von Umsatzsteuer, da Kleinunternehmer gemäß § 19 UStG.');
  }
  if (invoice.notes?.trim()) collected.push(invoice.notes.trim());
  if (company.invoiceFooterNote?.trim()) collected.push(company.invoiceFooterNote.trim());
  if (invoice.dunningLevel > 0) {
    collected.push(`Offener Betrag: ${formatAmount(invoice.grossTotalCents)} EUR.`);
  }
  return collected.length > 0 ? [collected.join('\n')] : [];
}

function firstLine(description: string): string {
  return description.split('\n')[0]?.trim() || 'Personenbeförderung';
}

function restLines(description: string): string {
  return description.split('\n').slice(1).join(' ').trim();
}

/**
 * The library types the country as the full ISO 3166-1 alpha-2 union. Our own
 * settings hold a free-text field, so the shape is checked here and anything
 * unrecognisable falls back to DE rather than producing an invalid document.
 */
type LibraryCountryCode =
  LibraryInvoice['ubl:Invoice']['cac:AccountingSupplierParty']['cac:Party']['cac:PostalAddress']['cac:Country']['cbc:IdentificationCode'];

function countryCode(country: string): LibraryCountryCode {
  const trimmed = country.trim().toUpperCase();
  return /^[A-Z]{2}$/.test(trimmed) ? (trimmed as LibraryCountryCode) : 'DE';
}

/**
 * The library reports schema violations through the logger and then throws a
 * ValidationError carrying only Ajv paths. Keeping the messages lets
 * `generateEInvoice` surface something diagnosable when a rule we did not
 * pre-flight is broken.
 */
const SILENT_LOGGER = {
  log: () => {},
  warn: (message: string) => console.warn('[e-invoice]', message),
  error: (message: string) => console.error('[e-invoice]', message),
};
